/**
 * FIGURES for a created exam: requested, reviewed, and only then used in the layout.
 *
 * WHY BEFORE THE LAYOUT. A figure's real dimensions decide how much of a page it takes, and a rejected
 * figure does not just change a picture — it changes the task ("label the diagram" has to become
 * something else) and therefore the points. Both have to be known before a line of the document exists.
 *
 * WHY A REVIEW AT ALL. Image models fail at exactly what a printed exam needs: they add text unbidden
 * (usually misspelled), produce colour renders that print as grey mud, and merge the structures a student
 * is meant to tell apart. None of that is visible in the prompt — only in the image.
 *
 * WHO REVIEWS: the creator, which is a context rather than a model. The review turn is given the plan —
 * the figure's purpose, the tasks it has to serve, and the acceptance criteria the planner wrote for
 * itself — and asked to decide as the teacher who ordered it. The reason it must be that and not a generic
 * image checker is that "is this figure good" has no answer without knowing what it was for: text in the
 * image is fatal on a labelling diagram and REQUIRED on a source-text excerpt. Knowing which needs the
 * brief, not a bigger model, so this runs on the cheap vision model (see `examImageReviewModel`) — the
 * criteria travel WITH the request, and the creator is held to what it asked for.
 *
 * A REVIEW CAN GIVE UP. `verdict: "abandon"` ends the attempts immediately: an image model that cannot
 * draw a thing will not learn to in two more tries, and the task being rewritten without a figure is a
 * normal outcome, not a failure. Burning three generations to reach the same place only costs money.
 *
 * TWO KINDS OF FIGURE, and only one of them involves coordinates:
 *
 *   'reference' — the common case. A diagram, illustration or source image a task refers to. Generate,
 *                 review, use. Nothing is located, nothing is drawn on top. IMAGE_ANALYSIS, a sketch to
 *                 interpret, a photo to describe: no answer goes on the figure at all.
 *
 *   'labelled'  — a LABEL_IMAGE task, where students write names against numbers printed ON the figure.
 *                 Those numbers cannot come from the image model (it misspells them, cannot be told to
 *                 renumber, and cannot translate them), so the template draws them — which means someone
 *                 has to say WHERE. That is the only reason a locate step exists.
 *
 * A FAILED LOCATE IS NOT A FAILED FIGURE. Locating structures in a generated diagram will not always
 * work, so it cannot gate anything: the figure stays usable, `callouts` comes back null, and the caller
 * writes the task without numbered callouts — students name the structures in a list instead of on the
 * diagram. Same figure, same points, one less affordance.
 *
 * UNVERIFIED AGAINST THE LIVE API. The request shape (`modalities`) and the response shape below follow
 * OpenRouter's image-output convention, and the router forwards both untouched (its OpenRouter handler is
 * a thin proxy onto the OpenAI SDK). This has not been run against the real model, so the response reader
 * accepts several plausible shapes and logs what it actually received rather than assuming.
 */

import { logger } from '@adornis/base/logging.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import {
  examImageLocateModel,
  examImageModalities,
  examImageModel,
  examImageReviewModel,
  examImageReviewTemperature,
  examImageSupportsNegativePrompt,
} from '../../../shared/models.js';
import { addCostToExam } from '../add-cost.js';
import { getLLMRouter } from '../llm/llm-gate.js';

/** How many times one figure may be regenerated before its task has to do without it. */
const MAX_IMAGE_ATTEMPTS = 3;

/**
 * The asset filename an accepted image gets, taken from what the model actually returned rather than
 * assumed. Gemini's image models (and, before them, WEBP-returning endpoints generally) do not always
 * answer with PNG bytes even though "an image" was requested — hardcoding ".png" regardless produced a
 * file typst could not decode ("Invalid PNG signature") whenever the real bytes were something else.
 */
export function extensionForImage(mimeType: string): string {
  const subtype = mimeType.split('/')[1]?.split('+')[0]?.toLowerCase();
  return subtype === 'jpeg' ? 'jpg' : subtype || 'png';
}

/**
 * Saves an accepted figure as its own retrievable `ImageFile`, in addition to the raw bytes returned for
 * the compile step — the compile only ever sees the bytes (they are staged as a typst shadow file and
 * discarded after), so without this an accepted figure would otherwise never be viewable on its own once
 * the exam is compiled (see `Exam.figureAssets`, populated from this by the caller in exam-creation-agent.ts).
 */
async function persistFigureFile(materialId: string, bytes: Buffer, mimeType: string): Promise<string> {
  const file = await ImageFile.createFromBuffer(bytes, `${materialId}.${extensionForImage(mimeType)}`);
  await file.save();
  return file._id;
}

/**
 * A labelling figure needs enough located structures to actually BE a diagram-labelling task — one or two
 * numbers out of eight requested is not a smaller version of that task, it is a different, worse one. Below
 * this share of `mustShow`, a locate result is treated the same as "locate failed entirely": worth
 * regenerating the image over (see `prepareOne`), and — if attempts run out — worth describing to the
 * source phase as unavailable rather than as a diagram with almost nothing on it (see `describeMaterials`
 * in exam-creation-agent.ts, which imports this same threshold so the two can never disagree).
 */
const MIN_CALLOUT_SHARE = 0.5;

export function meetsCalloutMinimum(callouts: Callout[] | null | undefined, required: number): boolean {
  if (required === 0) return true;
  if (!callouts || callouts.length === 0) return false;
  return callouts.length >= Math.max(1, Math.ceil(required * MIN_CALLOUT_SHARE));
}

export type FigureMode = 'reference' | 'labelled';

/**
 * Whether a figure carries text of its own — a per-figure decision, because both answers are right
 * depending on the task:
 *
 *   'none'     — the default and the common case. The layout owns every label, number and caption on the
 *                page, so text in the image can only collide with it, cannot be corrected, cannot be
 *                translated, and is usually misspelled.
 *   'labels'   — text that belongs to the drawing itself and would be absurd outside it: axis labels and
 *                units on a diagram, place names on a map, a legend.
 *   'verbatim' — the figure IS text-bearing material: a source excerpt, a newspaper clipping, a
 *                screenshot, a handwritten note to interpret. Here the text is the point.
 *
 * For 'labels' and 'verbatim' the exact strings belong in `imageText`, and the review checks them
 * character by character — an image model will happily render "Zeit (s)" as "Ziet (s)".
 */
export type FigureText = 'none' | 'labels' | 'verbatim';

export interface ImageRequest {
  /** Matches the blueprint material id, and becomes the asset filename. */
  id: string;
  /**
   * What to draw, ALWAYS IN ENGLISH regardless of the exam's own language: nothing here is printed, it
   * only talks to an image-generation model, so it only has to work for that model. Written by the
   * planner as one flowing, positively framed description — subject, style, everything that must be
   * distinguishable — that already depicts everything `mustShow` names in prose, not as a list (see
   * `mustShow` below for why the two fields are not merged here).
   */
  prompt: string;
  mode: FigureMode;
  /**
   * What the figure has to show, IN THE EXAM'S OWN LANGUAGE, not English — unlike every other field here.
   * For `labelled` this doubles as the LABEL_IMAGE master-solution key (`figure-block`'s `callouts` in
   * exam.typ records it as "1 -> Aorta"), graded against a student's answer written in that same
   * language; translating it would grade a German answer against an English key. For `reference` it is
   * just content requirements the review checks, kept in the same language for consistency. Never
   * injected into `prompt` as a list — the planner already folds it into that English description.
   */
  mustShow: string[];
  /** Free-text aspect hint ("portrait", "landscape", "square") — image models take words, not numbers. */
  aspect?: string;
  /** Text policy for this figure. Absent means 'none'; a `labelled` figure is forced to 'none'. */
  text?: FigureText;
  /** The exact strings that must appear in the image, spelled exactly this way. Only with 'labels'/'verbatim'. */
  imageText?: string[];
  /**
   * The creator's OWN acceptance criteria for this figure, in its own words and IN ENGLISH — nothing here
   * is printed either, it only talks to the vision model that reviews the result: what this image has to
   * do for the task it was planned for. Handed straight to the review, which is the same model — this is
   * how the agent holds itself to what it asked for instead of to a generic rubric.
   */
  acceptance?: string;
  /**
   * The plan as far as this figure is concerned: the exam, the figure's purpose, and the tasks that use it
   * with their wording and points. Injected into the review turn so it judges the figure against the exam
   * it belongs to rather than in isolation.
   */
  context?: string;
}

export interface Callout {
  label: string;
  /** Normalised 0..1 from the image's top-left, as `figure-block(callouts: …)` expects. */
  x: number;
  y: number;
}

export interface ImageReview {
  /**
   * 'accept' — use it. 'retry' — regenerate with `critique` as the correction. 'abandon' — stop trying;
   * the task gets rewritten without this figure.
   */
  verdict: 'accept' | 'retry' | 'abandon';
  /** Convenience for callers: `verdict === 'accept'`. */
  usable: boolean;
  /** Why it was rejected, phrased so it can be fed into the next generation attempt. */
  critique: string;
  containsText: boolean;
  /**
   * Whether the required `imageText` appears, correctly spelled. `null` when no text was required — the
   * usual case, and not the same as "the spelling is fine".
   */
  textCorrect: boolean | null;
  legibleInGreyscale: boolean;
  /** Content requirements the reviewer could not find in the image. */
  missing: string[];
}

export interface AcceptedImage {
  id: string;
  bytes: Buffer;
  mimeType: string;
  /** The persisted `ImageFile` id — the only way to show this figure standalone (see `ExamFigureAsset`). */
  fileId: string;
  review: ImageReview;
  /**
   * Where to draw the numbers, for a `labelled` figure whose structures could be located. `null` means
   * either a reference figure (never located) or a labelled one whose locate failed — in both cases the
   * figure is fine and the task simply gets no numbered callouts.
   */
  callouts: Callout[] | null;
  attempts: number;
}

export interface RejectedImage {
  id: string;
  /** The last critique — the reason the task has to be rewritten without this figure. */
  reason: string;
  attempts: number;
}

export interface ImageOutcome {
  accepted: AcceptedImage[];
  rejected: RejectedImage[];
}

/**
 * Generates and reviews every requested figure, locating callouts only where a labelling task needs
 * them, and reports which figures failed outright.
 *
 * A rejection is a normal outcome, not an error: the caller rewrites those tasks without a figure, which
 * is only cheap because no layout exists yet.
 *
 * `options.onItemFinished`, if given, fires once per figure as its own `prepareOne` call settles (accepted
 * or rejected), so the caller can report progress per figure rather than only once the whole batch lands.
 */
export async function prepareExamImages(
  examId: string,
  requests: ImageRequest[],
  options: { signal?: AbortSignal; onItemFinished?: () => void } = {},
): Promise<ImageOutcome> {
  const results = await Promise.all(
    requests.map(request => prepareOne(examId, resolveTextPolicy(examId, request), options).finally(() => options.onItemFinished?.())),
  );

  const accepted = results.filter((result): result is AcceptedImage => 'bytes' in result);
  const rejected = results.filter((result): result is RejectedImage => 'reason' in result);
  if (rejected.length > 0) {
    logger.warn({ examId, rejected: rejected.map(image => image.id) }, 'Exam figures rejected by review');
  }
  return { accepted, rejected };
}

/**
 * A labelling figure may not carry text, whatever the plan said.
 *
 * Not a matter of taste: the template draws the numbers students write against, and a figure that carries
 * its own text puts a second, uncontrollable set of markings under them. Enforced here rather than trusted
 * to the prompt, so the two answers can never disagree.
 */
function resolveTextPolicy(examId: string, request: ImageRequest): ImageRequest {
  const text = request.text ?? 'none';
  if (request.mode === 'labelled' && text !== 'none') {
    logger.info(
      { examId, id: request.id, requested: text },
      'Labelled figure asked for text — overridden to none, the layout owns the numbers',
    );
    return { ...request, text: 'none', imageText: [] };
  }
  return { ...request, text };
}

async function prepareOne(
  examId: string,
  request: ImageRequest,
  options: { signal?: AbortSignal },
): Promise<AcceptedImage | RejectedImage> {
  let critique = '';

  for (let attempt = 1; attempt <= MAX_IMAGE_ATTEMPTS; attempt += 1) {
    if (options.signal?.aborted) return { id: request.id, reason: 'aborted', attempts: attempt - 1 };

    // eslint-disable-next-line no-await-in-loop -- each attempt is a correction of the previous one
    const image = await generateImage(examId, request, critique);
    if (!image) {
      critique = 'the model returned no image';
      continue;
    }

    // eslint-disable-next-line no-await-in-loop -- the review decides whether another attempt is needed
    const review = await reviewImage(examId, request, image);
    if (review.verdict === 'abandon') {
      // The creator itself says another attempt will not help. Believe it — it wrote the brief.
      logger.info({ examId, id: request.id, attempt, critique: review.critique }, 'Exam figure abandoned by review');
      return { id: request.id, reason: review.critique, attempts: attempt };
    }
    if (!review.usable) {
      critique = review.critique;
      logger.info({ examId, id: request.id, attempt, critique }, 'Exam figure rejected, retrying');
      continue;
    }

    // Only now, and only for a labelling task. A reference figure is finished the moment it passes review.
    const callouts =
      request.mode === 'labelled' && request.mustShow.length > 0
        ? // eslint-disable-next-line no-await-in-loop -- runs once, after a figure has been accepted
          await locateCallouts(examId, request, image, review)
        : null;

    // Too few located structures is not "a smaller diagram" — it is not a usable labelling figure at all
    // (see MIN_CALLOUT_SHARE). Worth spending a remaining attempt on a clearer image rather than accepting
    // it: the review already said the image itself was fine, so what's missing is that the requested
    // structures were not distinguishable enough for the locate step to find them with confidence.
    if (
      request.mode === 'labelled' &&
      request.mustShow.length > 0 &&
      !meetsCalloutMinimum(callouts, request.mustShow.length) &&
      attempt < MAX_IMAGE_ATTEMPTS
    ) {
      // NOT `request.mustShow.join(', ')` here: those names are in the exam's own language, and `critique`
      // feeds straight into the next attempt's (English) `buildImagePrompt` — exactly the kind of
      // foreign-language injection removed from that prompt itself above. The count alone is enough
      // correction for the model to act on.
      critique =
        `Only ${callouts?.length ?? 0} of ${request.mustShow.length} requested structures could be located ` +
        `with confidence. Draw each one larger, more clearly separated from the others, and identifiable ` +
        'by its shape alone as that specific structure — not a generic outline standing in for it.';
      logger.info(
        { examId, id: request.id, attempt, located: callouts?.length ?? 0, required: request.mustShow.length },
        'Too few callouts located, regenerating the figure',
      );
      continue;
    }

    // eslint-disable-next-line no-await-in-loop -- runs once, after a figure has been accepted
    const fileId = await persistFigureFile(request.id, image.bytes, image.mimeType);
    return {
      id: request.id,
      bytes: image.bytes,
      mimeType: image.mimeType,
      fileId,
      review,
      callouts,
      attempts: attempt,
    };
  }

  return { id: request.id, reason: critique || 'no usable figure produced', attempts: MAX_IMAGE_ATTEMPTS };
}

// ─── generation ─────────────────────────────────────────────────────────────────────────────────────

interface GeneratedImage {
  bytes: Buffer;
  mimeType: string;
}

/**
 * The prompt is assembled here rather than taken verbatim from the plan, because the constraints that make
 * a figure usable ON PAPER are properties of the medium, not of this exam: a school printer is monochrome,
 * and a page has a fixed width.
 *
 * WHAT IS NOT A PROPERTY OF THE MEDIUM is whether the figure carries text. That is the creator's call per
 * figure (see `FigureText`), so it is asked of the request rather than hard-coded — an exam whose material
 * is a newspaper clipping needs the text, and a labelling diagram must not have any.
 */

function buildImagePrompt(request: ImageRequest, critique: string): string {
  const parts = [
    request.prompt,
    ...textInstructions(request),
    'High contrast black on a white background — it will be printed in greyscale.',
    'No shading, no gradients, no photographic detail.',
  ];
  // NOT a rendering of `mustShow` here: that field stays in the exam's own language (it doubles as the
  // LABEL_IMAGE master-solution key — see exam-creation-agent.ts's PlannedMaterial), so injecting it into
  // an otherwise-English prompt would hand the model a foreign-language list to typeset — exactly the
  // caption-shaped input that caused it to render labels in the first place. The planner now writes
  // `request.prompt` itself as one flowing, positively framed ENGLISH description that already depicts
  // everything `mustShow` names, so nothing has to be re-injected here — only the generic, name-free
  // distinguishability rule a labelling figure needs regardless of what it depicts.
  if (request.mode === 'labelled' && request.mustShow.length > 0) {
    parts.push(
      // NO LEADER LINES: asking the image model to draw its own pointer lines (and asking the locate step
      // to find their tips) was tried and reverted — the locate model consistently reported finding
      // nothing, even against images the review had already accepted. Locating a point ON or beside a
      // structure is a task the vision model can actually do; the template now draws its own leader line
      // from that point out to a fixed answer box (see figure-block in exam.typ), so precision there
      // matters far less than it did when a bare circle had to land exactly on the mark.
      'Every distinct structure the description above depicts must be clearly separated from the others, ' +
        'large enough for a callout point to land on it, and identifiable by its shape alone — two ' +
        'different structures must never look interchangeable.',
    );
  }
  if (request.aspect) parts.push(`Orientation: ${request.aspect}.`);
  if (critique) parts.push(`A previous attempt was rejected for this reason — fix it: ${critique}`);
  // LAST WORD, deliberately: with a long prompt the closing instruction is the one that survives, and
  // unwanted lettering is the single most common reason a first attempt is rejected.
  if (request.text === 'none') {
    parts.push('UNDER NO CIRCUMSTANCES may the finished image contain any text. ABSOLUTELY ZERO characters.');
  }
  return parts.join('\n');
}

/** The text half of the prompt, which is the half that differs per figure. */
function textInstructions(request: ImageRequest): string[] {
  const wanted = request.imageText?.filter(Boolean) ?? [];
  const spelling =
    wanted.length > 0
      ? [
          `The image must contain exactly this text, spelled EXACTLY as written, with no additions: ${wanted
            .map(entry => `"${entry}"`)
            .join(', ')}.`,
          'Render the text in a plain, clearly legible printed typeface. Misspelling it makes the image unusable.',
        ]
      : [];

  switch (request.text) {
    case 'verbatim':
      return [
        'Style: a clean reproduction of a printed document, legible at half a page in width.',
        ...(wanted.length > 0
          ? spelling
          : ['The text shown must be plain, clearly legible and correctly spelled printed text.']),
        'No decorative borders, no captions describing the image, no watermarks.',
      ];
    case 'labels':
      return [
        'Style: clean schematic line art suitable for a school worksheet.',
        ...spelling,
        'Apart from that text: no title, no caption, no legend, no numbering, no lettering of any kind.',
      ];
    default:
      // POSITIVE GENRE-FRAMING, backed by an absolutist prohibition — not a replacement for it. Whether
      // naming "text" here helps or hurts is a live, unresolved question for THIS specific model (flux,
      // a T5-encoder model with unusually strong instruction-following — plausibly closer to actually
      // parsing a hard prohibition than the CLIP-encoder models the "negation backfires" folklore was
      // built against) and can only be settled by watching real generations, not by reasoning about it
      // again. Establishing the genre costs nothing either way, so it stays regardless of which theory
      // wins.
      return [
        'Style: a pure line-art colouring-book illustration.',
        'Visuals: only structural outlines and empty white space.',
        'The drawing consists exclusively of continuous black ink lines on a plain white background.',
        'STRICTLY FORBIDDEN, under no circumstances: any text, letters, digits, words, labels, captions, ' +
          'legends, titles, numbers or watermarks anywhere in the image. ABSOLUTELY ZERO characters of any ' +
          'kind, anywhere on the page.',
      ];
  }
}

/**
 * A native negative-prompt channel: real classifier-free-guidance input the model was TRAINED to steer
 * away from, not prose to interpret (the STRICTLY FORBIDDEN clauses elsewhere stay too — complementary,
 * not either/or). Sent only when `examImageSupportsNegativePrompt` is true. Per text policy, since "no
 * text" is the wrong negative for a figure that was asked to carry some.
 */
function buildNegativePrompt(request: ImageRequest): string {
  const universal = 'blurry, low quality, photographic, photorealistic, shading, gradient, jpeg artifacts, noise';
  const perPolicy =
    request.text === 'verbatim'
      ? 'misspelled text, garbled text, illegible text, decorative border, watermark'
      : request.text === 'labels'
        ? 'title, caption, legend, watermark, extra unrequested text, stray lettering'
        : 'text, letters, words, numbers, digits, labels, captions, titles, legends, numbering, watermark, signature, writing, typography, annotations';
  return `${universal}, ${perPolicy}`;
}

async function generateImage(examId: string, request: ImageRequest, critique: string): Promise<GeneratedImage | null> {
  const router = getLLMRouter({}, 'batch');
  try {
    const response = await router.chat.completions.create({
      stream: false,
      model: examImageModel,
      provider: 'openrouter',
      messages: [{ role: 'user', content: buildImagePrompt(request, critique) }],
      // Not part of the chat-completions type, forwarded verbatim by the router — the same mechanism the
      // generation agents use for `reasoning`. Kept next to the model itself in models.ts, not hardcoded
      // here, because it has to match whichever model that is (see the comment there).
      ...({
        modalities: examImageModalities,
        ...(examImageSupportsNegativePrompt ? { negative_prompt: buildNegativePrompt(request) } : {}),
      } as any),
    });

    await addCostToExam(response, examId, 'examImage');
    return readImage(response, request.id);
  } catch (err) {
    logger.warn({ err, examId, id: request.id }, 'Exam figure generation call failed');
    return null;
  }
}

/**
 * Pulls the image bytes out of the response.
 *
 * Deliberately tolerant. The documented OpenRouter shape is `message.images[].image_url.url` carrying a
 * data URI, but a non-standard field is exactly what an SDK version can move, and this has not been run
 * against the live model. Anything unrecognised is logged with the shape it saw, so a mismatch is a
 * one-line fix rather than a mystery.
 */
function readImage(response: any, id: string): GeneratedImage | null {
  const message = response?.choices?.[0]?.message;
  const candidates = [
    ...(Array.isArray(message?.images) ? message.images : []),
    ...(Array.isArray(message?.content) ? message.content : []),
  ];

  for (const candidate of candidates) {
    const url =
      candidate?.image_url?.url ?? candidate?.imageUrl?.url ?? (typeof candidate === 'string' ? candidate : undefined);
    const parsed = typeof url === 'string' ? parseDataUri(url) : null;
    if (parsed) return parsed;
    if (typeof candidate?.b64_json === 'string') {
      return { bytes: Buffer.from(candidate.b64_json, 'base64'), mimeType: 'image/png' };
    }
  }

  logger.warn(
    { id, messageKeys: message ? Object.keys(message) : null, candidateCount: candidates.length },
    'No image found in the image-model response — check the response shape against OpenRouter',
  );
  return null;
}

function parseDataUri(url: string): GeneratedImage | null {
  // eslint-disable-next-line require-unicode-regexp -- `v` needs an es2024 target; the project is es2020
  const match = /^data:(?<mime>image\/[\w.+-]+);base64,(?<data>.+)$/u.exec(url);
  if (!match?.groups?.data) return null;
  return { bytes: Buffer.from(match.groups.data, 'base64'), mimeType: match.groups.mime ?? 'image/png' };
}

// ─── review: the creator looking at what it asked for ──────────────────────────────────────────────

/**
 * Deliberately NOT a rubric. The one thing this review has that a generic image checker does not is the
 * plan — what the figure is for, which tasks use it, and what the creator itself said would make it
 * usable — so the prompt hands those over and asks for a decision, rather than listing criteria that
 * would then contradict the brief. The only absolutes left are the ones that come from paper.
 */
function reviewSystemPrompt(request: ImageRequest): string {
  const textRule =
    request.text === 'verbatim'
      ? `THIS FIGURE IS TEXT-BEARING MATERIAL. Text in it is required, not a defect. Judge whether the text
is complete, correctly spelled and legible at half a page in width; wrong or garbled text makes it
unusable, and so does text you did not ask for.`
      : request.text === 'labels'
        ? `THIS FIGURE MAY CARRY THE LABELS LISTED BELOW and nothing else. Those labels must be present and
correctly spelled. Any further text — a title, a caption, a legend, stray lettering — makes it unusable,
because the layout owns everything else on the page.`
        : `THIS FIGURE MUST CARRY NO TEXT AT ALL. Every label, number and caption on the page is drawn by the
layout, so text in the image collides with it and cannot be corrected or translated. Any letter, digit or
word in the image makes it unusable.${
            request.mode === 'labelled'
              ? ` Students will write against numbers printed beside this figure, connected to it by a line
the layout draws itself — so the required structures must be individually distinguishable and separated
enough for a point to land clearly on or next to each one. The image itself carries no lines, dots,
numbers or any other marking of its own — those all belong to the layout.`
              : ''
          }`;

  return `You are the teacher who planned this exam, looking at a figure that has just been generated for one
of your own tasks. You wrote the brief, so you decide whether this image does the job — not whether it is
a nice picture.

${textRule}

IT WILL BE PRINTED, in black and white, on a school printer, at roughly half to two thirds of a page
wide. Anything that depends on colour, fine shading or fine detail is unusable however good it looks on
screen.

THREE VERDICTS, and choosing the right one matters:
- "accept": it does the job. Small imperfections that a student would never notice are not defects.
- "retry": it is wrong in a way a corrected prompt could plausibly fix. Say what to change, as an
  instruction to the image model.
- "abandon": no amount of retrying will produce a usable figure of this kind (the model cannot draw it, or
  what you asked for was not something an image model can produce). This is a NORMAL outcome — the task
  will be rewritten without a figure, keeping its points. Choosing it early saves attempts that would
  fail the same way.

"STRUCTURES STUDENTS WILL HAVE TO NAME" / "IT MUST SHOW" below is in the exam's own language, not English
— it is the printed answer key, so it has to stay that way. Your "critique" and "missing", by contrast, go
straight into the NEXT generation attempt's English prompt, so write them IN ENGLISH regardless, and
describe what is wrong rather than quoting those own-language names back verbatim — a quoted name is
foreign-language text landing inside an English instruction, the same problem as handing the image model a
list of names to typeset.

Answer with JSON only:
{"verdict": "accept" | "retry" | "abandon",
 "critique": "what is wrong with THIS image, and only that, in English — do not restate standing
  instructions (like 'no text') unless THIS image actually violates them; repeating them when they are not
  the problem just reintroduces the words into the next generation attempt (empty when accepting)",
 "containsText": boolean, "textCorrect": boolean, "legibleInGreyscale": boolean,
 "missing": ["something the brief required that is not in the image, described in English"]}`;
}

/** What the plan says about this one figure — the part of the creator's own context it needs back. */
function reviewUserPrompt(request: ImageRequest): string {
  const parts = [`YOUR BRIEF FOR THIS FIGURE:\n${request.prompt}`];
  if (request.context) parts.push(`WHERE IT IS USED:\n${request.context}`);
  if (request.acceptance) parts.push(`WHAT YOU SAID WOULD MAKE IT USABLE:\n${request.acceptance}`);
  if (request.mustShow.length > 0) {
    parts.push(
      request.mode === 'labelled'
        ? `STRUCTURES STUDENTS WILL HAVE TO NAME (each must be identifiable and separable): ${request.mustShow.join(', ')}`
        : `IT MUST SHOW: ${request.mustShow.join(', ')}`,
    );
  }
  const wanted = request.imageText?.filter(Boolean) ?? [];
  if (wanted.length > 0) {
    parts.push(`TEXT THAT MUST APPEAR, spelled exactly: ${wanted.map(entry => `"${entry}"`).join(', ')}`);
  }
  return parts.join('\n\n');
}

async function reviewImage(examId: string, request: ImageRequest, image: GeneratedImage): Promise<ImageReview> {
  try {
    const response = await visionCall(examId, {
      model: examImageReviewModel,
      systemPrompt: reviewSystemPrompt(request),
      userPrompt: reviewUserPrompt(request),
      image,
      costLabel: 'examImageReview',
    });
    return parseReview(request, response);
  } catch (err) {
    // A review that could not run is not a pass. Failing closed costs one regeneration; failing open puts
    // an unreviewed figure on a printed exam.
    logger.warn({ err, examId, id: request.id }, 'Exam figure review failed');
    return failedReview('the review could not be completed');
  }
}

function failedReview(critique: string): ImageReview {
  return {
    verdict: 'retry',
    usable: false,
    critique,
    containsText: false,
    textCorrect: null,
    legibleInGreyscale: false,
    missing: [],
  };
}

/**
 * The verdict is the model's, and the overrides are only the ones the request itself settles.
 *
 * Text is the whole reason this is not a fixed rule: it is fatal when the figure was asked to have none,
 * and required when it was asked to carry some. Reading that off the request means a creator that wanted a
 * source-text image gets one, and a creator that wanted a bare diagram cannot be talked into text by a
 * lenient review.
 */
function parseReview(request: ImageRequest, raw: unknown): ImageReview {
  const parsed = parseJsonObject(raw);
  if (!parsed) return failedReview('the review was not valid JSON');

  const verdict =
    parsed.verdict === 'accept' || parsed.verdict === 'abandon' || parsed.verdict === 'retry'
      ? parsed.verdict
      : // An unreadable verdict is not an acceptance. `usable` is accepted as an alias because it is the
        // word an older prompt used and a model that has seen both will sometimes answer with it.
        parsed.usable === true
        ? 'accept'
        : 'retry';

  const containsText = parsed.containsText === true;
  const textRequired = (request.imageText?.filter(Boolean) ?? []).length > 0;
  const textCorrect = textRequired ? parsed.textCorrect !== false : null;
  const missing = (Array.isArray(parsed.missing) ? parsed.missing : []).map(String).filter(Boolean);

  const fatal =
    (request.text === 'none' && containsText) ||
    (textRequired && textCorrect === false) ||
    (request.text === 'verbatim' && !containsText) ||
    missing.length > 0;

  // A critique that came back empty has to be replaced, not passed on: for an abandoned figure it becomes
  // the reason the source phase is given for rewriting the task, and an empty reason there reads as "no
  // rejection at all" — the task would then be written around a figure that does not exist.
  const stated = typeof parsed.critique === 'string' ? parsed.critique.trim() : '';

  return {
    verdict: verdict === 'abandon' ? 'abandon' : fatal ? 'retry' : verdict,
    usable: verdict === 'accept' && !fatal,
    critique: stated || defaultCritique(request, verdict, { containsText, textCorrect, missing }),
    containsText,
    textCorrect,
    legibleInGreyscale: parsed.legibleInGreyscale !== false,
    missing,
  };
}

/** Only used when the review rejected something without saying why — the next attempt still needs a reason. */
function defaultCritique(
  request: ImageRequest,
  verdict: ImageReview['verdict'],
  findings: { containsText: boolean; textCorrect: boolean | null; missing: string[] },
): string {
  const { containsText, textCorrect, missing } = findings;
  if (verdict === 'abandon') return 'no usable figure of this kind could be produced';
  if (request.text === 'none' && containsText) return 'the image contains text; it must contain none';
  if (textCorrect === false) return 'the required text is missing or misspelled';
  if (request.text === 'verbatim' && !containsText) return 'the image contains no text, but it must show the text';
  // NOT `missing.join(', ')`: those entries can echo back the brief's own-language content requirements,
  // and this critique feeds straight into the next attempt's English prompt (same reasoning as the
  // callout-count critique above) — the count is enough to prompt a retry without importing that risk.
  if (missing.length > 0) return `the image is missing ${missing.length} of the required elements from the brief`;
  return 'rejected without a stated reason';
}

// ─── locate: only for a labelling task, and allowed to fail ─────────────────────────────────────────

const LOCATE_SYSTEM_PROMPT = `You mark where leader lines will point on a diagram for a school
exam. The leader lines and answer sections will be drawn by the layout, so you only have to place a single point on each structure.

For each requested structure, give one point ON the structure itself. Never place two points on top of one another.

Coordinates use your normal 0-1000 scale, origin at the image's TOP-LEFT: x and y each an integer between
0 and 1000.

If you cannot confidently identify a structure in this image, LEAVE IT OUT rather than guessing — a number
pointing at the wrong place is worse than no number at all.
Still return all the structures you could identify.

Answer with JSON only:
{"locations": [{"label": "exactly as requested", "x": 485, "y": 673}, ...]}`;

/** The scale `LOCATE_SYSTEM_PROMPT` asks for — this model's native coordinate space for spatial tasks. */
const LOCATE_COORDINATE_SCALE = 1000;

async function locateCallouts(
  examId: string,
  request: ImageRequest,
  image: GeneratedImage,
  review: ImageReview,
): Promise<Callout[] | null> {
  try {
    const response = await visionCall(examId, {
      model: examImageLocateModel,
      systemPrompt: LOCATE_SYSTEM_PROMPT,
      userPrompt: `Place a callout for each of these structures: ${request.mustShow.join(', ')}`,
      image,
      costLabel: 'examImageLocate',
    });
    const parsed = parseJsonObject(response);
    const rawLocations = Array.isArray(parsed?.locations) ? parsed.locations : [];
    // /LOCATE_COORDINATE_SCALE: the model answers on its own native 0-1000 spatial-task scale (confirmed
    // against real output — it ignored a plain "0 to 1" instruction and returned 0-1000 regardless), so the
    // prompt now asks for that scale explicitly and this is where it gets normalised to the 0-1 range
    // `Callout` and `figure-block` in exam.typ expect.
    const callouts: Callout[] = rawLocations
      .map((entry: any) => ({
        label: String(entry?.label ?? ''),
        x: Number(entry?.x) / LOCATE_COORDINATE_SCALE,
        y: Number(entry?.y) / LOCATE_COORDINATE_SCALE,
      }))
      .filter((entry: Callout) => entry.label.length > 0 && inUnitRange(entry.x) && inUnitRange(entry.y));

    // PARTIAL IS KEPT, not discarded. A figure numbered 1..4 when six structures were asked for is not a
    // broken figure — it is a task worth 4 points instead of 6, decided in the source phase from exactly
    // this count (see describeMaterials). Discarding the partial result would throw away real work for no
    // reason: the four found callouts are exactly as usable as if six had been requested to begin with.
    if (callouts.length < request.mustShow.length) {
      logger.info(
        { examId, id: request.id, located: callouts.length, required: request.mustShow.length },
        'Partial locate — passing the found callouts forward, task points follow from their count',
      );

      // So the source phase can also see which structures did NOT make it, and ask students to name those
      // in a list instead of on the diagram, rather than silently dropping them from the exam.
      const foundLabels = new Set(callouts.map(c => c.label.toLowerCase()));
      review.missing = request.mustShow.filter(req => !foundLabels.has(req.toLowerCase()));
    }

    return callouts;
  } catch (err) {
    logger.warn({ err, examId, id: request.id }, 'Callout location failed — figure kept without callouts');
    return null;
  }
}

const inUnitRange = (value: number): boolean => Number.isFinite(value) && value >= 0 && value <= 1;

// ─── shared plumbing ────────────────────────────────────────────────────────────────────────────────

interface VisionCall {
  model: string;
  systemPrompt: string;
  userPrompt: string;
  image: GeneratedImage;
  costLabel: string;
}

async function visionCall(examId: string, call: VisionCall): Promise<unknown> {
  const { model, systemPrompt, userPrompt, image, costLabel } = call;
  const router = getLLMRouter({}, 'batch');
  const response = await router.chat.completions.create({
    stream: false,
    model,
    provider: 'openrouter',
    temperature: examImageReviewTemperature,
    response_format: { type: 'json_object' } as any,
    messages: [
      { role: 'system', content: systemPrompt },
      {
        role: 'user',
        content: [
          { type: 'text', text: userPrompt },
          {
            type: 'image_url',
            image_url: { url: `data:${image.mimeType};base64,${image.bytes.toString('base64')}` },
          },
        ],
      },
    ],
  });

  await addCostToExam(response, examId, costLabel);
  return (response as any)?.choices?.[0]?.message?.content;
}

function parseJsonObject(raw: unknown): Record<string, any> | null {
  const text = typeof raw === 'string' ? raw : ((raw as any)?.content ?? '');
  if (!String(text).trim()) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
